import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useGetItemDocumentsMutation } from "../../api/360FilesAPI";
import { LegalCaseItem } from "../docs/LegalCase";
import { NewsItem } from "../docs/News";
import { DiligenceItem } from "../docs/Diligence";
import { Document, Page } from "react-pdf";
import { PersonInfoApi } from "../../api/Person";
import { highlightNamesInText } from "../../utils/nameHighlighting";

import SkeletonSpacer from "../../shared-components/SkeletonSpacer";

export function Summary360LegalDocument(
  props: LegalCaseItem & { personEx?: PersonInfoApi }
) {
  const {
    mutate: getItemDocuments,
    isLoading,
    isIdle,
    data: uploadedFiles,
  } = useGetItemDocumentsMutation();

  useEffect(() => {
    getItemDocuments(props.external_id);
  }, [props.external_id, getItemDocuments]);

  const pdfUrls = useMemo(() => {
    return (
      uploadedFiles
        ?.filter((file) => file.filename.toLowerCase().endsWith(".pdf"))
        .map((file) => file.url) ?? []
    );
  }, [uploadedFiles]);

  const isLoadingUrl = isIdle || isLoading;

  const titleSegments = useMemo(() => {
    return highlightNamesInText(props.title || "", props.personEx);
  }, [props.title, props.personEx]);

  return (
    <div className="min-w-[800px] max-w-[1000px]">
      <div className="w-full">
        <div className="text-4xl font-bold text-gray-800 mb-4 pr-8 relative">
          {titleSegments.map((segment, index) =>
            segment.highlight ? (
              <mark key={index} className="bg-yellow-200 font-bold">
                {segment.text}
              </mark>
            ) : (
              <span key={index}>{segment.text}</span>
            )
          )}
        </div>
        <div>
          <TitleWithValue
            title="Start Date"
            value={props.start_date}
            personEx={props.personEx}
          />
          {props.end_date != null && props.end_date !== "" && (
            <TitleWithValue
              title="End Date"
              value={props.end_date}
              personEx={props.personEx}
            />
          )}
          <TitleWithValue
            title="Court"
            value={props.court}
            personEx={props.personEx}
          />
          <TitleWithValue
            title="Case Number"
            value={props.case_number}
            personEx={props.personEx}
          />
        </div>
      </div>
      <div className="mt-8">
        <Summary360PDF pdfUrls={pdfUrls} isLoadingUrl={isLoadingUrl} />
      </div>
    </div>
  );
}

export function Summary360DiligenceDocument(
  props: DiligenceItem & { personEx?: PersonInfoApi }
) {
  const {
    mutate: getItemDocuments,
    isLoading,
    isIdle,
    data: uploadedFiles,
  } = useGetItemDocumentsMutation();

  useEffect(() => {
    getItemDocuments(props.external_id);
  }, [props.external_id, getItemDocuments]);

  const pdfUrls = useMemo(() => {
    return (
      uploadedFiles
        ?.filter((file) => file.filename.toLowerCase().endsWith(".pdf"))
        .map((file) => file.url) ?? []
    );
  }, [uploadedFiles]);

  const isLoadingUrl = isIdle || isLoading;

  const titleSegments = useMemo(() => {
    return highlightNamesInText(props.name || "", props.personEx);
  }, [props.name, props.personEx]);

  const bodySegments = useMemo(() => {
    const text = props.body || "";
    const withNormalizedBreaks = text
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<p(?:\s[^>]*)?>/gi, "");
    const cleanText = withNormalizedBreaks.replace(/<[^>]*>/g, "");
    return highlightNamesInText(cleanText, props.personEx);
  }, [props.body, props.personEx]);

  return (
    <div className="min-w-[800px] max-w-[1000px]">
      <div className="w-full">
        <div className="text-4xl font-bold text-gray-800 mb-4 pr-8 relative">
          {titleSegments.map((segment, index) =>
            segment.highlight ? (
              <mark key={index} className="bg-yellow-200 font-bold">
                {segment.text}
              </mark>
            ) : (
              <span key={index}>{segment.text}</span>
            )
          )}
        </div>
        <div>
          <TitleWithValue
            title="Source"
            value={props.source}
            personEx={props.personEx}
          />
          {props.date != null && props.date !== "" && (
            <TitleWithValue
              title="Date"
              value={props.date}
              personEx={props.personEx}
            />
          )}
        </div>
      </div>

      {props.body && (
        <div className="mt-8 text-gray-500">
          {bodySegments.map((segment, index) =>
            segment.highlight ? (
              <mark key={index} className="bg-yellow-200 font-bold underline">
                {segment.text
                  .split(/\n/)
                  .flatMap((line, i) =>
                    i === 0 ? [line] : [<br key={`${index}-${i}`} />, line]
                  )}
              </mark>
            ) : (
              <span key={index}>
                {segment.text
                  .split(/\n/)
                  .flatMap((line, i) =>
                    i === 0 ? [line] : [<br key={`${index}-${i}`} />, line]
                  )}
              </span>
            )
          )}
        </div>
      )}

      {(isLoadingUrl || pdfUrls.length > 0) && (
        <div className="mt-8 break-before-page print:break-before-page">
          <Summary360PDF pdfUrls={pdfUrls} isLoadingUrl={isLoadingUrl} />
        </div>
      )}
    </div>
  );
}

export function Summary360NewsDocument(
  props: NewsItem & { personEx?: PersonInfoApi }
) {
  const {
    mutate: getItemDocuments,
    isLoading,
    isIdle,
    data: uploadedFiles,
  } = useGetItemDocumentsMutation();

  useEffect(() => {
    getItemDocuments(props.external_id);
  }, [props.external_id, getItemDocuments]);

  const pdfUrls = useMemo(() => {
    return (
      uploadedFiles
        ?.filter((file) => file.filename.toLowerCase().endsWith(".pdf"))
        .map((file) => file.url) ?? []
    );
  }, [uploadedFiles]);

  const isLoadingUrl = isIdle || isLoading;

  const titleSegments = useMemo(() => {
    return highlightNamesInText(props.title || "", props.personEx);
  }, [props.title, props.personEx]);

  const bodySegments = useMemo(() => {
    const text = props.body || "";
    // Normalize HTML line breaks to newlines before stripping tags, so we don't lose breaks.
    // Only </p> becomes \n so that <p>A</p><p>B</p> → "A\nB\n" (no leading/trailing or doubled breaks).
    const withNormalizedBreaks = text
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<p(?:\s[^>]*)?>/gi, "");
    // Strip remaining HTML tags (insecure or presentational)
    const cleanText = withNormalizedBreaks.replace(/<[^>]*>/g, "");
    // Keep newlines as \n; we render them as <br /> in React to avoid dangerouslySetInnerHTML
    return highlightNamesInText(cleanText, props.personEx);
  }, [props.body, props.personEx]);

  return (
    <div className="min-w-[800px] max-w-[1000px]">
      <div className="w-full">
        <div className="text-4xl font-bold text-gray-800 mb-4 pr-44 relative">
          {titleSegments.map((segment, index) =>
            segment.highlight ? (
              <mark key={index} className="bg-yellow-200 font-bold">
                {segment.text}
              </mark>
            ) : (
              <span key={index}>{segment.text}</span>
            )
          )}
        </div>
        <div>
          <TitleWithValue
            title="URL"
            value={props.url ?? "None"}
            personEx={props.personEx}
          />
          <TitleWithValue
            title="Author"
            value={props.author}
            personEx={props.personEx}
          />
          <TitleWithValue
            title="Date"
            value={props.date}
            personEx={props.personEx}
          />
        </div>
      </div>

      <div className="mt-8">
        {props.body && (
          <div className="text-gray-500">
            {bodySegments.map((segment, index) =>
              segment.highlight ? (
                <mark key={index} className="bg-yellow-200 font-bold underline">
                  {segment.text
                    .split(/\n/)
                    .flatMap((line, i) =>
                      i === 0 ? [line] : [<br key={`${index}-${i}`} />, line]
                    )}
                </mark>
              ) : (
                <span key={index}>
                  {segment.text
                    .split(/\n/)
                    .flatMap((line, i) =>
                      i === 0 ? [line] : [<br key={`${index}-${i}`} />, line]
                    )}
                </span>
              )
            )}
          </div>
        )}
      </div>

      {(isLoadingUrl || pdfUrls.length > 0) && (
        <div className="mt-8 break-before-page print:break-before-page">
          <Summary360PDF pdfUrls={pdfUrls} isLoadingUrl={isLoadingUrl} />
        </div>
      )}
    </div>
  );
}

function TitleWithValue({
  title,
  value,
  personEx,
}: {
  title: string;
  value: string | undefined;
  personEx?: PersonInfoApi;
}) {
  const valueSegments = useMemo(() => {
    if (!value) return [{ text: value || "", highlight: false }];
    return highlightNamesInText(value, personEx);
  }, [value, personEx]);

  return (
    <div className="flex gap-2">
      <div className="font-bold text-gray-600">{title}:</div>
      <div className="text-gray-500">
        {valueSegments.map((segment, index) =>
          segment.highlight ? (
            <mark key={index} className="bg-yellow-200 font-bold">
              {segment.text}
            </mark>
          ) : (
            <span key={index}>{segment.text}</span>
          )
        )}
      </div>
    </div>
  );
}

function Summary360PDF(props: { pdfUrls: string[]; isLoadingUrl: boolean }) {
  const { pdfUrls, isLoadingUrl } = props;

  if (isLoadingUrl) {
    return <Summary360LoadingSpacer />;
  }

  return (
    <>
      {pdfUrls.map((pdfUrl) => (
        <Summary360PDFDocument key={pdfUrl} pdfUrl={pdfUrl} />
      ))}
    </>
  );
}

function Summary360PDFDocument({ pdfUrl }: { pdfUrl: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string>("");
  const [numPages, setNumPages] = useState<number>(0);

  const handleLoadSuccess = useCallback((data: { numPages: number }) => {
    setNumPages(data.numPages);
  }, []);

  useEffect(() => {
    setError("");
    setNumPages(0);
  }, [pdfUrl]);

  if (error) {
    return <div className="text-center text-gray-500">Error loading PDF</div>;
  }

  return (
    <div ref={containerRef} className="h-full w-full overflow-y-auto">
      <Document
        file={pdfUrl}
        onLoadSuccess={handleLoadSuccess}
        onLoadError={(error: Error) => {
          console.error("Error loading PDF:", error);
          setError("Failed to load PDF");
        }}
        loading={<Summary360LoadingSpacer />}
        className="flex flex-col items-center bg-white"
      >
        {Array.from({ length: numPages }, (_, i) => (
          <Page
            key={`page_${i + 1}`}
            pageNumber={i + 1}
            width={800}
            className="mb-4"
          />
        ))}
      </Document>
    </div>
  );
}

function Summary360LoadingSpacer() {
  return (
    <div className="w-full bg-white-500 text-gray-500 h-40">
      <div className="w-full p-4 flex flex-col items-center gap-2">
        <SkeletonSpacer />
        <SkeletonSpacer />
        <SkeletonSpacer />
        <SkeletonSpacer />
      </div>
    </div>
  );
}
